<script setup lang="ts">
import { buildAmenitiesLd, type AmenitiesPage } from "~/utils/amenitiesSchema";

const { locale, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

const { data: page } = await useAsyncData(
  `page-amenities-${locale.value}`,
  async () => {
    const collection = ("pages_" + locale.value) as "pages_en";
    return await queryCollection(collection)
      .where("slug", "=", "amenities")
      .first();
  },
  { watch: [locale] }
);

useLocalisedSeo(page);

// JSON-LD structured data: LodgingBusiness-linked TouristAttractions (pools,
// aquapark, beach), Restaurants with hours + menu URLs, plus BreadcrumbList
// and a WebPage node with dateModified sourced from the content frontmatter.
useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: computed(() =>
        page.value
          ? JSON.stringify(
              buildAmenitiesLd(page.value as AmenitiesPage, {
                pagePath: route.path,
                homePath: localePath("/"),
                homeLabel: t("nav.home"),
                pageTitle:
                  (page.value as AmenitiesPage).seo_title ||
                  (page.value as AmenitiesPage).title,
              }),
            )
          : "",
      ),
    },
  ],
});

// Phones often miss the default reveal threshold during fast finger-scroll —
// sections flick past while still at opacity:0. Trigger ~280px before the
// section enters the viewport on small screens; keep default on tablet/desktop.
useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);
</script>

<template>
  <div v-if="page">
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden py-10 sm:py-14 md:min-h-[52vh] md:py-20"
    >
      <NuxtImg
        v-if="page.hero_image"
        :src="page.hero_image"
        :alt="page.hero_image_alt ?? page.title"
        loading="eager"
        fetchpriority="high"
        format="webp"
        sizes="xs:100vw sm:100vw md:100vw lg:1280px xl:1536px"
        class="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div class="container relative z-10 text-center">
        <h1
          class="mt-6 font-display text-3xl italic leading-tight text-white drop-shadow sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl"
        >
          {{ page.title }}
        </h1>
        <p
          v-if="page.intro"
          class="mx-auto mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl"
        >
          {{ page.intro }}
        </p>
      </div>
      <div
        class="absolute inset-x-0 bottom-5 z-10 flex justify-center px-4 sm:bottom-7 md:bottom-10"
      >
        <NuxtLink
          :to="localePath('/search')"
          class="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-5 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.14em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300 sm:gap-2 sm:px-7 sm:py-3 sm:text-sm sm:tracking-[0.18em]"
        >
          {{ $t('amenities.cta.view_apartments') }}
          <svg
            class="h-3.5 w-3.5 sm:h-4 sm:w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </NuxtLink>
      </div>
    </section>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <div v-if="page.pool_showcase" class="cv-auto">
      <AmenitiesPoolShowcase :block="page.pool_showcase" />
    </div>

    <div v-if="page.apartments_bridge" class="cv-auto">
      <AmenitiesApartmentsBridge :block="page.apartments_bridge" />
    </div>

    <div v-if="page.beach_showcase" class="cv-auto">
      <AmenitiesBeachShowcase :block="page.beach_showcase" />
    </div>

    <div v-if="page.aquapark_showcase" class="cv-auto">
      <AmenitiesAquaparkShowcase :block="page.aquapark_showcase" />
    </div>

    <div v-if="page.restaurants_showcase" class="cv-auto">
      <AmenitiesRestaurantsShowcase :block="page.restaurants_showcase" />
    </div>

    <div v-if="page.kids_zones" class="cv-auto">
      <AmenitiesKidsZonesShowcase :block="page.kids_zones" />
    </div>

    <div v-if="page.activities" class="cv-auto">
      <ServicesActivityGrid :block="page.activities" />
    </div>

    <div v-if="page.grounds_showcase" class="cv-auto">
      <AmenitiesGroundsShowcase :block="page.grounds_showcase" />
    </div>

    <section
      class="cv-auto relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-12 md:py-16 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-24 left-1/3 h-64 w-64 rounded-full bg-lime-200/20 blur-3xl dark:bg-emerald-600/5"
      />
      <div data-reveal class="relative container mx-auto max-w-3xl px-4 text-center">
        <h2
          class="font-display text-3xl italic leading-tight text-slate-900 md:text-4xl dark:text-white"
        >
          {{ $t('amenities.cta.final.heading') }}
        </h2>
        <p
          class="mt-4 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ $t('amenities.cta.final.subtext') }}
        </p>
        <div class="mt-8">
          <NuxtLink
            :to="localePath('/search')"
            class="inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 font-body text-sm font-semibold uppercase tracking-[0.18em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300"
          >
            {{ $t('amenities.cta.view_apartments') }}
            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </section>

  </div>
</template>
